import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";

const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const DesignRequest = lazy(() => import("./pages/DesignRequest"));
const Offers = lazy(() => import("./pages/Offers"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminOffers = lazy(() => import("./pages/admin/AdminOffers"));
const AdminTasks = lazy(() => import("./pages/admin/AdminTasks"));
const AdminContactSettings = lazy(() => import("./pages/admin/AdminContactSettings"));
const AdminCurrency = lazy(() => import("./pages/admin/AdminCurrency"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAppointments = lazy(() => import("./pages/admin/AdminAppointments"));
const BookAppointment = lazy(() => import("./pages/BookAppointment"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const LoyaltyProgram = lazy(() => import("./pages/LoyaltyProgram"));
const Referral = lazy(() => import("./pages/Referral"));
const Blog = lazy(() => import("./pages/Blog"));
const ProjectMap = lazy(() => import("./pages/ProjectMap"));
const EmergencyBooking = lazy(() => import("./pages/EmergencyBooking"));
const Compare = lazy(() => import("./pages/Compare"));
const AIDesign = lazy(() => import("./pages/AIDesign"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/من-نحن" element={<About />} />
            <Route path="/خدماتنا" element={<Services />} />
            <Route path="/معرض-الأعمال" element={<Gallery />} />
            <Route path="/تواصل-معنا" element={<Contact />} />
            <Route path="/طلب-تصميم" element={<DesignRequest />} />
            <Route path="/سياسة-الخصوصية" element={<Privacy />} />
            <Route path="/شروط-الاستخدام" element={<Terms />} />
            <Route path="/العروض" element={<Offers />} />
            <Route path="/حجز-موعد" element={<BookAppointment />} />
            <Route path="/تتبع-الطلب" element={<TrackOrder />} />
            <Route path="/برنامج-الولاء" element={<LoyaltyProgram />} />
            <Route path="/الإحالة" element={<Referral />} />
            <Route path="/المدونة" element={<Blog />} />
            <Route path="/المدونة/:slug" element={<Blog />} />
            <Route path="/خريطة-المشاريع" element={<ProjectMap />} />
            <Route path="/طوارئ" element={<EmergencyBooking />} />
            <Route path="/مقارنة" element={<Compare />} />
            <Route path="/تصميم-ذكي" element={<AIDesign />} />
            <Route path="/تثبيت" element={<Install />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Dashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/messages" element={<AdminMessages />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/content" element={<AdminContent />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/offers" element={<AdminOffers />} />
            <Route path="/admin/tasks" element={<AdminTasks />} />
            <Route path="/admin/contact-settings" element={<AdminContactSettings />} />
            <Route path="/admin/currency" element={<AdminCurrency />} />
            <Route path="/admin/audit" element={<AdminAudit />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/appointments" element={<AdminAppointments />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
